import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  X,
  Pencil,
  FileText,
  PlayCircle,
  Plus,
  Trash2,
  UserRound,
  BriefcaseBusiness,
  GraduationCap,
} from "lucide-react";
import ProfileHero from "./components/ProfileHero";
import ProfileCompletion from "./components/ProfileCompletion";
import AboutCard from "./components/AboutCard";
import SkillsCard from "./components/SkillsCard";
import ExperienceCard from "./components/ExperienceCard";
import EducationCard from "./components/EducationCard";
import ResumeCard from "./components/ResumeCard";
import VideoCard from "./components/VideoCard";
import useCandidateProfile from "../../hooks/useCandidateProfile";
import { uploadCandidateProfileResume } from "../../api/candidateProfile.api";
import type {
  CandidateProfileData,
  ProfileExperienceItem,
  ProfileEducationItem,
  ProfileSkillItem,
} from "../../types/candidateProfile.types";

type EditSection =
  | "basic"
  | "about"
  | "skills"
  | "experience"
  | "education"
  | "resume"
  | "video";

const sections: { id: EditSection; label: string; icon: typeof UserRound }[] = [
  { id: "basic", label: "Basic Information", icon: UserRound },
  { id: "about", label: "About Me", icon: FileText },
  { id: "skills", label: "Skills", icon: BriefcaseBusiness },
  { id: "experience", label: "Work Experience", icon: BriefcaseBusiness },
  { id: "education", label: "Education", icon: GraduationCap },
  { id: "resume", label: "Resume", icon: FileText },
  { id: "video", label: "Video Profile", icon: PlayCircle },
];

const inputClass =
  "mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100";
const labelClass = "block text-xs font-medium text-slate-700";

function Field({
  label,
  value,
  onChange,
  required,
  type = "text",
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  type?: string;
  placeholder?: string;
}) {
  return (
    <label className={labelClass}>
      {label} {required && <span className="text-red-500">*</span>}
      <input
        className={inputClass}
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder || label}
      />
    </label>
  );
}

function TextAreaField({
  label,
  value,
  onChange,
  required,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
}) {
  return (
    <label className={labelClass}>
      {label} {required && <span className="text-red-500">*</span>}
      <textarea
        className={`${inputClass} min-h-32 resize-y`}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

export default function Profile() {
  const navigate = useNavigate();
  const {
    profile,
    loading,
    error,
    saveProfile,
    uploadVideo,
    videoUploading,
    videoUploadError,
  } = useCandidateProfile();
  const [isEditing, setIsEditing] = useState(false);
  const [activeSection, setActiveSection] = useState<EditSection>("basic");
  const [savedProfile, setSavedProfile] = useState<CandidateProfileData | null>(
    null
  );
  const [draft, setDraft] = useState<CandidateProfileData | null>(null);
  const [skillsText, setSkillsText] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [videoUploadSuccess, setVideoUploadSuccess] = useState(false);
  const [localVideoUploadError, setLocalVideoUploadError] = useState<string | null>(null);
  const [resumeUploading, setResumeUploading] = useState(false);
  const [resumeUploadError, setResumeUploadError] = useState<string | null>(null);
  const [resumeUploadSuccess, setResumeUploadSuccess] = useState(false);

  useEffect(() => {
    if (profile && !isEditing) {
      setSavedProfile(profile);
    }
  }, [profile, isEditing]);

  const currentProfile = savedProfile || profile;

  const safeProfile = currentProfile
    ? {
      ...currentProfile,
      photoUrl: currentProfile.photoUrl || "",
      designation: currentProfile.designation || "",
      phone: currentProfile.phone || "",
      location: currentProfile.location || "",
      about: currentProfile.about || "",
      skills: currentProfile.skills || [],
      experience: currentProfile.experience || [],
      education: currentProfile.education || [],
      stats: currentProfile.stats || {
        experience: "",
        projects: "",
        skillsCount: String(currentProfile.skills?.length || 0),
        status: "Candidate",
      },
      completionSteps: currentProfile.completionSteps || [],
      profileCompletionPercent: currentProfile.profileCompletionPercent || 0,
      resume: currentProfile.resume || null,
      video: currentProfile.video || null,
    }
    : null;

  const openEditor = () => {
    if (!currentProfile) return;
    const copy = JSON.parse(JSON.stringify(currentProfile)) as CandidateProfileData;
    setDraft({
      ...copy,
      stats: copy.stats || { experience: "", projects: "", skillsCount: "", status: "Candidate" },
      skills: copy.skills || [],
      experience: copy.experience || [],
      education: copy.education || [],
    });
    setSkillsText((copy.skills || []).map((skill: ProfileSkillItem) => skill.name).join(", "));
    setSaveError(null);
    setSaveSuccess(false);
    setActiveSection("basic");
    setIsEditing(true);
  };

  const openEditorAt = (section: EditSection) => {
    openEditor();
    setActiveSection(section);
  };

  const closeEditor = () => {
    if (isSaving || videoUploading) return;
    setIsEditing(false);
    setDraft(null);
    setSaveError(null);
    setSaveSuccess(false);
  };

  const updateDraft = <K extends keyof CandidateProfileData>(
    key: K,
    value: CandidateProfileData[K]
  ) => {
    setDraft((previous) => (previous ? { ...previous, [key]: value } : previous));
  };

  const handleProfileVideoUpload = async (file?: File) => {
    if (!file) return;

    setLocalVideoUploadError(null);
    setVideoUploadSuccess(false);

    const maxFileSize = 100 * 1024 * 1024;
    const allowedExtensions = [".mp4", ".mov", ".avi", ".mkv", ".webm"];
    const allowedMimeTypes = [
      "video/mp4",
      "video/quicktime",
      "video/x-msvideo",
      "video/x-matroska",
      "video/webm",
    ];
    const fileName = file.name.toLowerCase();
    const hasAllowedExtension = allowedExtensions.some((extension) =>
      fileName.endsWith(extension)
    );

    if (!hasAllowedExtension) {
      setLocalVideoUploadError("Choose an MP4, MOV, AVI, MKV, or WEBM video file.");
      return;
    }

    if (file.type && !allowedMimeTypes.includes(file.type)) {
      setLocalVideoUploadError("This video format is not supported.");
      return;
    }

    if (file.size > maxFileSize) {
      setLocalVideoUploadError("Video size must be 100 MB or less.");
      return;
    }

    try {
      const uploadedVideo = await uploadVideo(file);

      setSavedProfile((previous) =>
        previous ? { ...previous, video: uploadedVideo } : previous
      );
      setDraft((previous) =>
        previous ? { ...previous, video: uploadedVideo } : previous
      );
      setVideoUploadSuccess(true);
    } catch (uploadError: unknown) {
      const message =
        typeof uploadError === "string"
          ? uploadError
          : uploadError instanceof Error
            ? uploadError.message
            : "Unable to upload your introduction video. Please try again.";

      setLocalVideoUploadError(message);
    }
  };

  const handleProfileResumeUpload = async (file: File) => {
    if (resumeUploading) return;

    setResumeUploadError(null);
    setResumeUploadSuccess(false);

    try {
      setResumeUploading(true);

      const result = await uploadCandidateProfileResume(file);
      const uploadedResume = result.resume;

      setSavedProfile((previous) => {
        const base = previous ?? profile;
        return base ? { ...base, resume: uploadedResume } : previous;
      });

      setDraft((previous) =>
        previous ? { ...previous, resume: uploadedResume } : previous
      );

      setResumeUploadSuccess(true);
    } catch (uploadError: unknown) {
      const message =
        uploadError instanceof Error
          ? uploadError.message
          : "Unable to upload your resume. Please try again.";

      setResumeUploadError(message);
    } finally {
      setResumeUploading(false);
    }
  };

  const updateExperience = (
    index: number,
    key: keyof ProfileExperienceItem,
    value: string | boolean
  ) => {
    if (!draft) return;
    updateDraft(
      "experience",
      draft.experience.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [key]: value } : item
      )
    );
  };

  const updateEducation = (
    index: number,
    key: keyof ProfileEducationItem,
    value: string
  ) => {
    if (!draft) return;
    updateDraft(
      "education",
      draft.education.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [key]: value } : item
      )
    );
  };

  const saveDraftLocally = async () => {
    if (!draft || isSaving) return;

    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    const skills: ProfileSkillItem[] = skillsText
      .split(",")
      .map((name) => name.trim())
      .filter(Boolean)
      .map((name) => {
        const existing = (draft.skills || []).find(
          (skill: ProfileSkillItem) => skill.name.toLowerCase() === name.toLowerCase()
        );
        return { name, level: existing?.level || "Intermediate" };
      });

    try {
      // Persist only fields currently supported by the candidate-profile API.
      const responseProfile = await saveProfile({
        designation: draft.designation || "",
        phone: draft.phone || "",
        location: draft.location || "",
        about: draft.about || "",
        skills,
        experience: draft.experience || [],
        education: draft.education || [],
      });

      // Keep UI-only fields in memory while using server values for persisted fields.
      const mergedProfile: CandidateProfileData = {
        ...draft,
        ...responseProfile,
        name: responseProfile.name ?? draft.name,
        email: responseProfile.email ?? draft.email,
        photoUrl: draft.photoUrl || "",
        stats: {
          experience: draft.stats?.experience || "",
          projects: draft.stats?.projects || "",
          skillsCount: String(skills.length),
          status: draft.stats?.status || "Candidate",
        },
        profileCompletionPercent: responseProfile.profileCompletionPercent ?? draft.profileCompletionPercent ?? 0,
        completionSteps: responseProfile.completionSteps ?? draft.completionSteps ?? [],
        resume: draft.resume ?? null,
        video: draft.video ?? null,
        skills: responseProfile.skills ?? skills,
        experience: responseProfile.experience ?? draft.experience ?? [],
        education: responseProfile.education ?? draft.education ?? [],
      };

      setSavedProfile(mergedProfile);
      setIsEditing(false);
      setDraft(null);
      setSaveSuccess(true);
    } catch (saveError: unknown) {
      const message =
        typeof saveError === "string"
          ? saveError
          : saveError instanceof Error
            ? saveError.message
            : "Unable to save your profile. Please try again.";

      setSaveError(message);
    } finally {
      setIsSaving(false);
    }
  };

  if (loading && !safeProfile) {
    return (
      <div className="flex min-h-[300px] items-center justify-center">
        <p className="text-sm text-slate-500">Loading profile...</p>
      </div>
    );
  }

  if (!safeProfile) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-10 text-center">
        <p className="text-sm text-red-600">
          {error || "Profile not found."}
        </p>
        <button
          type="button"
          onClick={() => navigate("/candidate")}
          className="mt-4 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  const completion = Math.min(
    100,
    Math.max(0, Number(safeProfile?.profileCompletionPercent) || 0)
  );

  return (
    <div className="mx-auto w-full max-w-7xl space-y-5 pb-8">
      <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">My Profile</h1>
          <p className="mt-1 text-sm text-slate-500">
            View and manage your personal and professional information.
          </p>
        </div>
        <button
          type="button"
          onClick={openEditor}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700"
        >
          <Pencil size={16} />
          Edit Profile
        </button>
      </div>

      {/* Existing reusable profile components */}
      <ProfileHero
        profile={safeProfile}
        onEdit={openEditor}
      />

      <ProfileCompletion
        percent={completion}
        steps={safeProfile.completionSteps}
      />

      {saveSuccess && !isEditing && (
        <div
          role="status"
          className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700"
        >
          Profile changes saved successfully.
        </div>
      )}

      {saveError && !isEditing && (
        <div
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
        >
          {saveError}
        </div>
      )}

      <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-3">
        <div className="min-w-0 space-y-5 lg:col-span-2">
          <AboutCard
            about={safeProfile.about || "Add a short professional summary."}
            stats={safeProfile.stats}
            onEdit={() => openEditorAt("about")}
          />

          <SkillsCard
            skills={safeProfile.skills}
            onEdit={() => openEditorAt("skills")}
            onAdd={() => openEditorAt("skills")}
          />

          <ExperienceCard
            experience={safeProfile.experience}
            onEdit={() => openEditorAt("experience")}
            onAdd={() => openEditorAt("experience")}
          />

          <EducationCard
            education={safeProfile.education}
            onEdit={() => openEditorAt("education")}
            onAdd={() => openEditorAt("education")}
          />
        </div>

        <aside className="min-w-0 space-y-5">
          <div className="space-y-3">
            <ResumeCard
              resume={safeProfile.resume}
              onUpload={handleProfileResumeUpload}
            />

            {resumeUploading && (
              <p role="status" className="text-sm text-blue-600">
                Uploading resume...
              </p>
            )}

            {resumeUploadError && (
              <p role="alert" className="text-sm text-red-600">
                {resumeUploadError}
              </p>
            )}

            {resumeUploadSuccess && (
              <p role="status" className="text-sm text-green-600">
                Resume uploaded successfully.
              </p>
            )}
          </div>
          <VideoCard
            video={safeProfile.video}
            onManage={() => openEditorAt("video")}
          />
        </aside>
      </div>

      {/* Edit Profile drawer */}
      {isEditing && draft && (
        <div className="fixed inset-0 z-[100] flex justify-end">
          <button
            type="button"
            aria-label="Close edit profile"
            onClick={closeEditor}
            className="absolute inset-0 bg-slate-900/40 backdrop-blur-[1px]"
          />

          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="edit-profile-title"
            className="relative z-10 flex h-full w-full max-w-3xl flex-col bg-white shadow-2xl"
          >
            <header className="flex items-start justify-between border-b border-slate-200 px-5 py-4 sm:px-7">
              <div>
                <h2 id="edit-profile-title" className="text-lg font-bold text-slate-900">
                  Edit Profile
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  Update your personal and professional information.
                </p>
              </div>
              <button
                type="button"
                onClick={closeEditor}
                aria-label="Close"
                className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
              >
                <X size={20} />
              </button>
            </header>

            <div className="flex min-h-0 flex-1">
              <nav className="hidden w-44 shrink-0 space-y-1 overflow-y-auto border-r border-slate-200 p-3 sm:block">
                {sections.map((section) => {
                  const Icon = section.icon;
                  const active = activeSection === section.id;
                  return (
                    <button
                      key={section.id}
                      type="button"
                      onClick={() => setActiveSection(section.id)}
                      className={`flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left text-xs font-medium transition ${active
                          ? "bg-blue-50 text-blue-700 ring-1 ring-blue-100"
                          : "text-slate-600 hover:bg-slate-50"
                        }`}
                    >
                      <Icon size={15} />
                      <span>{section.label}</span>
                    </button>
                  );
                })}
              </nav>

              <form
                id="candidate-profile-form"
                className="min-h-0 flex-1 overflow-y-auto p-5 sm:p-6"
                onSubmit={(event) => {
                  event.preventDefault();
                  void saveDraftLocally();
                }}
              >
                {saveError && (
                  <div role="alert" className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700">
                    {saveError}
                  </div>
                )}
                {saveSuccess && (
                  <div role="status" className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm text-emerald-700">
                    Profile changes saved successfully.
                  </div>
                )}
                <div className="mb-5 sm:hidden">
                  <label className={labelClass}>
                    Edit section
                    <select
                      className={inputClass}
                      value={activeSection}
                      onChange={(event) =>
                        setActiveSection(event.target.value as EditSection)
                      }
                    >
                      {sections.map((section) => (
                        <option key={section.id} value={section.id}>
                          {section.label}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>

                {activeSection === "basic" && (
                  <div className="space-y-5">
                    <h3 className="text-base font-bold text-slate-900">Basic Information</h3>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div className="sm:col-span-2">
                        <Field label="Full Name" required value={draft.name} onChange={(value) => updateDraft("name", value)} />
                      </div>
                      <div className="sm:col-span-2">
                        <Field label="Designation" value={draft.designation} onChange={(value) => updateDraft("designation", value)} />
                      </div>
                      <Field label="Email Address" required type="email" value={draft.email} onChange={(value) => updateDraft("email", value)} />
                      <Field label="Phone Number" required value={draft.phone} onChange={(value) => updateDraft("phone", value)} />
                      <Field label="Location" required value={draft.location} onChange={(value) => updateDraft("location", value)} />
                      <Field label="Profile Photo URL" value={draft.photoUrl} onChange={(value) => updateDraft("photoUrl", value)} />
                      <div className="sm:col-span-2">
                        <label className={labelClass}>
                          Current Status
                          <select
                            className={inputClass}
                            value={draft.stats?.status || "Candidate"}
                            onChange={(event) =>
                              updateDraft("stats", {
                                experience: draft.stats?.experience || "",
                                projects: draft.stats?.projects || "",
                                skillsCount: draft.stats?.skillsCount || "",
                                status: event.target.value,
                              })
                            }
                          >
                            <option>Open to Work</option>
                            <option>Currently Employed</option>
                            <option>Not Looking</option>
                          </select>
                        </label>
                      </div>
                    </div>
                  </div>
                )}

                {activeSection === "about" && (
                  <div className="space-y-4">
                    <h3 className="text-base font-bold text-slate-900">About Me</h3>
                    <TextAreaField label="Professional Summary" required value={draft.about} onChange={(value) => updateDraft("about", value)} />
                    <p className="text-right text-xs text-slate-400">{draft.about.length}/1000</p>
                  </div>
                )}

                {activeSection === "skills" && (
                  <div className="space-y-4">
                    <h3 className="text-base font-bold text-slate-900">Skills</h3>
                    <label className={labelClass}>
                      Skills (comma separated) <span className="text-red-500">*</span>
                      <textarea
                        className={`${inputClass} min-h-28`}
                        value={skillsText}
                        onChange={(event) => setSkillsText(event.target.value)}
                        placeholder="React, TypeScript, Node.js"
                      />
                    </label>
                    <p className="text-xs text-slate-500">
                      Separate each skill with a comma. Existing skill levels are preserved when the skill name matches.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {skillsText.split(",").map((skill: string) => skill.trim()).filter(Boolean).map((skill: string, index: number) => (
                        <span key={`${skill}-${index}`} className="rounded-md bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {activeSection === "experience" && (
                  <div className="space-y-5">
                    <div className="flex items-center justify-between gap-3">
                      <h3 className="text-base font-bold text-slate-900">Work Experience</h3>
                      <button
                        type="button"
                        onClick={() =>
                          updateDraft("experience", [
                            ...draft.experience,
                            { company: "", role: "", duration: "", location: "", current: false },
                          ])
                        }
                        className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-700"
                      >
                        <Plus size={15} /> Add Experience
                      </button>
                    </div>
                    {draft.experience.map((item: ProfileExperienceItem, index: number) => (
                      <div key={index} className="space-y-3 rounded-xl border border-slate-200 p-4">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-semibold text-slate-800">Experience {index + 1}</p>
                          <button
                            type="button"
                            onClick={() => updateDraft("experience", draft.experience.filter((_, i) => i !== index))}
                            aria-label={`Remove experience ${index + 1}`}
                            className="rounded-md p-1.5 text-red-500 hover:bg-red-50"
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                          <Field label="Job Title" value={item.role} onChange={(value) => updateExperience(index, "role", value)} />
                          <Field label="Company" value={item.company} onChange={(value) => updateExperience(index, "company", value)} />
                          <Field label="Duration" value={item.duration} onChange={(value) => updateExperience(index, "duration", value)} />
                          <Field label="Location" value={item.location} onChange={(value) => updateExperience(index, "location", value)} />
                        </div>
                        <label className="flex items-center gap-2 text-sm text-slate-700">
                          <input type="checkbox" checked={item.current} onChange={(event) => updateExperience(index, "current", event.target.checked)} className="rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
                          I currently work here
                        </label>
                      </div>
                    ))}
                    {draft.experience.length === 0 && <p className="text-sm text-slate-500">No experience entries. Use Add Experience to add one.</p>}
                  </div>
                )}

                {activeSection === "education" && (
                  <div className="space-y-5">
                    <div className="flex items-center justify-between gap-3">
                      <h3 className="text-base font-bold text-slate-900">Education</h3>
                      <button
                        type="button"
                        onClick={() =>
                          updateDraft("education", [
                            ...draft.education,
                            { degree: "", institute: "", duration: "", location: "", score: "" },
                          ])
                        }
                        className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-700"
                      >
                        <Plus size={15} /> Add Education
                      </button>
                    </div>
                    {draft.education.map((item: ProfileEducationItem, index: number) => (
                      <div key={index} className="space-y-3 rounded-xl border border-slate-200 p-4">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-semibold text-slate-800">Education {index + 1}</p>
                          <button
                            type="button"
                            onClick={() => updateDraft("education", draft.education.filter((_, i) => i !== index))}
                            aria-label={`Remove education ${index + 1}`}
                            className="rounded-md p-1.5 text-red-500 hover:bg-red-50"
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                          <Field label="Degree" value={item.degree} onChange={(value) => updateEducation(index, "degree", value)} />
                          <Field label="Institute" value={item.institute} onChange={(value) => updateEducation(index, "institute", value)} />
                          <Field label="Duration" value={item.duration} onChange={(value) => updateEducation(index, "duration", value)} />
                          <Field label="Location" value={item.location} onChange={(value) => updateEducation(index, "location", value)} />
                          <Field label="Grade / Score" value={item.score} onChange={(value) => updateEducation(index, "score", value)} />
                        </div>
                      </div>
                    ))}
                    {draft.education.length === 0 && <p className="text-sm text-slate-500">No education entries. Use Add Education to add one.</p>}
                  </div>
                )}

                {activeSection === "resume" && (
                  <div className="space-y-4">
                    <h3 className="text-base font-bold text-slate-900">Resume</h3>
                    <p className="text-sm text-slate-500">
                      Resume upload and storage need to be connected to the backend. For this UI step, you can update the displayed resume details below.
                    </p>
                    <Field label="File Name" value={draft.resume?.fileName || ""} onChange={(value) => updateDraft("resume", draft.resume ? { ...draft.resume, fileName: value } : { fileName: value, fileType: "PDF", fileSize: "", fileUrl: "", atsScore: 0 })} />
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <Field label="File Type" value={draft.resume?.fileType || ""} onChange={(value) => updateDraft("resume", draft.resume ? { ...draft.resume, fileType: value } : { fileName: "", fileType: value, fileSize: "", fileUrl: "", atsScore: 0 })} />
                      <Field label="File Size" value={draft.resume?.fileSize || ""} onChange={(value) => updateDraft("resume", draft.resume ? { ...draft.resume, fileSize: value } : { fileName: "", fileType: "", fileSize: value, fileUrl: "", atsScore: 0 })} />
                    </div>
                    <Field label="Resume URL" value={draft.resume?.fileUrl || ""} onChange={(value) => updateDraft("resume", draft.resume ? { ...draft.resume, fileUrl: value } : { fileName: "", fileType: "PDF", fileSize: "", fileUrl: value, atsScore: 0 })} />
                    <label className={labelClass}>
                      Choose resume file (UI only)
                      <input type="file" accept=".pdf,.doc,.docx" className={`${inputClass} file:mr-3 file:rounded-md file:border-0 file:bg-blue-50 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-blue-700`} />
                    </label>
                  </div>
                )}

                {activeSection === "video" && (
                  <div className="space-y-4">
                    <h3 className="text-base font-bold text-slate-900">Video Profile</h3>
                    <p className="text-sm text-slate-500">
                      Upload one introduction video to your candidate profile. You can replace it at any time; uploading a video does not require saving the rest of your profile.
                    </p>

                    {draft.video?.videoUrl && (
                      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                        <p className="text-sm font-semibold text-slate-800">Current introduction video</p>
                        {draft.video.originalFileName && (
                          <p className="mt-1 break-all text-xs text-slate-500">
                            {draft.video.originalFileName}
                          </p>
                        )}
                        <a
                          href={draft.video.videoUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="mt-2 inline-flex text-sm font-medium text-blue-600 hover:text-blue-700"
                        >
                          Open current video
                        </a>
                      </div>
                    )}

                    <label className={labelClass}>
                      {draft.video ? "Replace introduction video" : "Choose introduction video"}
                      <input
                        type="file"
                        accept=".mp4,.mov,.avi,.mkv,.webm,video/mp4,video/quicktime,video/x-msvideo,video/x-matroska,video/webm"
                        disabled={videoUploading}
                        onChange={(event) => {
                          const file = event.currentTarget.files?.[0];
                          void handleProfileVideoUpload(file);
                          event.currentTarget.value = "";
                        }}
                        className={`${inputClass} file:mr-3 file:rounded-md file:border-0 file:bg-blue-50 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-blue-700 disabled:cursor-not-allowed disabled:opacity-60`}
                      />
                    </label>
                    <p className="text-xs text-slate-500">
                      Supported formats: MP4, MOV, AVI, MKV, WEBM. Maximum file size: 100 MB.
                    </p>

                    {(localVideoUploadError || videoUploadError) && (
                      <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700">
                        {localVideoUploadError || videoUploadError}
                      </div>
                    )}

                    {videoUploading && (
                      <div role="status" className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2.5 text-sm text-blue-700">
                        Uploading your introduction video. Please keep this page open.
                      </div>
                    )}

                    {videoUploadSuccess && !videoUploading && (
                      <div role="status" className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm text-emerald-700">
                        Introduction video uploaded successfully.
                      </div>
                    )}
                  </div>
                )}
              </form>
            </div>

            <footer className="flex items-center justify-end gap-3 border-t border-slate-200 px-5 py-4 sm:px-7">
              <button
                type="button"
                onClick={closeEditor}
                disabled={isSaving || videoUploading}
                className="rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="submit"
                form="candidate-profile-form"
                disabled={isSaving || videoUploading}
                className="rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSaving ? "Saving..." : "Save Changes"}
              </button>
            </footer>
          </section>
        </div>
      )}
    </div>
  );
}